import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/lib/supabase';
import { PropertyImageUpload, type ImageFile } from '@/components/dashboard/Property/PropertyImageUpload';

const formSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório'),
  description: z.string().min(1, 'Descrição é obrigatória'),
  price: z.string().min(1, 'Preço é obrigatório'),
  rent_price: z.string().optional(),
  area: z.string().min(1, 'Área é obrigatória'),
  bedrooms: z.string().min(1, 'Número de quartos é obrigatório'),
  bathrooms: z.string().min(1, 'Número de banheiros é obrigatório'),
  suites: z.string().min(1, 'Número de suítes é obrigatório'),
  parking_spaces: z.string().min(1, 'Número de vagas é obrigatório'),
  type: z.enum(['venda', 'aluguel', 'venda_aluguel']),
  status: z.enum(['disponivel', 'vendido', 'alugado']),
  category: z.enum(['Lançamento', 'Na planta', 'Em Construção', 'Pronto para morar']),
  address: z.string().min(1, 'Endereço é obrigatório'),
  city: z.string().min(1, 'Cidade é obrigatória'),
  state: z.string().min(1, 'Estado é obrigatório'),
  zip_code: z.string().min(1, 'CEP é obrigatório'),
  featured_offer_price: z.string().optional(),
  featured_offer_installment: z.string().optional(),
  featured_offer_description: z.string().optional(),
  is_visible_on_storefront: z.boolean().default(true),
});

export default function EditListingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [images, setImages] = useState<ImageFile[]>([]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      price: '',
      rent_price: '',
      area: '',
      bedrooms: '',
      bathrooms: '',
      suites: '0',
      parking_spaces: '',
      type: 'venda',
      status: 'disponivel',
      category: 'Pronto para morar',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      featured_offer_price: '',
      featured_offer_installment: '',
      featured_offer_description: '',
      is_visible_on_storefront: true,
    },
  });

  useEffect(() => {
    loadProperty();
  }, [id]);

  const loadProperty = async () => {
    try {
      setIsLoading(true);

      // Load property with images
      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .select(`
          *,
          property_images (
            id,
            url,
            is_featured
          )
        `)
        .eq('id', id)
        .single();

      if (propertyError) throw propertyError;

      // Format currency values
      const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      };

      // Convert existing images to ImageFile format
      const existingImages: ImageFile[] = property.property_images?.map((img: any) => ({
        id: img.id,
        url: img.url,
        isFeatured: img.is_featured,
        isExisting: true
      })) || [];

      setImages(existingImages);

      form.reset({
        title: property.title,
        description: property.description,
        price: formatCurrency(property.price),
        rent_price: property.rent_price ? formatCurrency(property.rent_price) : '',
        area: property.area.toString(),
        bedrooms: property.bedrooms.toString(),
        bathrooms: property.bathrooms.toString(),
        suites: (property.suites || 0).toString(),
        parking_spaces: property.parking_spaces.toString(),
        type: property.type,
        status: property.status,
        category: property.category || 'Pronto para morar',
        address: property.address,
        city: property.city,
        state: property.state,
        zip_code: property.zip_code,
        featured_offer_price: property.featured_offer_price ? formatCurrency(property.featured_offer_price) : '',
        featured_offer_installment: property.featured_offer_installment ? formatCurrency(property.featured_offer_installment) : '',
        featured_offer_description: property.featured_offer_description || '',
        is_visible_on_storefront: property.is_visible_on_storefront ?? true,
      });
    } catch (error) {
      toast.error('Erro ao carregar imóvel');
      navigate('/dashboard/listings');
    } finally {
      setIsLoading(false);
    }
  };

  const uploadNewImages = async (propertyId: string) => {
    const newImages = images.filter(img => !img.isExisting && img.file);
    
    const uploadPromises = newImages.map(async (image, index) => {
      if (!image.file) return null;

      const fileExt = image.file.name.split('.').pop();
      const fileName = `${propertyId}-${Date.now()}-${index}.${fileExt}`;
      const filePath = `property-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('public')
        .upload(filePath, image.file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('public')
        .getPublicUrl(filePath);

      return {
        property_id: propertyId,
        url: publicUrl,
        is_featured: image.isFeatured
      };
    });

    const uploadedImages = await Promise.all(uploadPromises);
    const validImages = uploadedImages.filter(Boolean);

    if (validImages.length > 0) {
      const { error: insertError } = await supabase
        .from('property_images')
        .insert(validImages);

      if (insertError) throw insertError;
    }

    return validImages;
  };

  const updateExistingImages = async () => {
    const existingImages = images.filter(img => img.isExisting);
    
    const updatePromises = existingImages.map(async (image) => {
      const { error } = await supabase
        .from('property_images')
        .update({ is_featured: image.isFeatured })
        .eq('id', image.id);

      if (error) throw error;
    });

    await Promise.all(updatePromises);
  };

  const deleteRemovedImages = async () => {
    // Get current image IDs from database
    const { data: currentImages } = await supabase
      .from('property_images')
      .select('id, url')
      .eq('property_id', id);

    if (!currentImages) return;

    // Find images that were removed
    const currentImageIds = images.filter(img => img.isExisting).map(img => img.id);
    const imagesToDelete = currentImages.filter(img => !currentImageIds.includes(img.id));

    if (imagesToDelete.length > 0) {
      // Delete from storage
      const deleteStoragePromises = imagesToDelete.map(async (image) => {
        const fileName = image.url.split('/').pop();
        if (fileName) {
          await supabase.storage
            .from('public')
            .remove([`property-images/${fileName}`]);
        }
      });

      await Promise.all(deleteStoragePromises);

      // Delete from database
      const { error } = await supabase
        .from('property_images')
        .delete()
        .in('id', imagesToDelete.map(img => img.id));

      if (error) throw error;
    }
  };

  const handleDeleteProperty = async () => {
    try {
      setIsDeleting(true);

      // Delete all property images from storage first
      const { data: propertyImages } = await supabase
        .from('property_images')
        .select('url')
        .eq('property_id', id);

      if (propertyImages && propertyImages.length > 0) {
        const deleteStoragePromises = propertyImages.map(async (image) => {
          const fileName = image.url.split('/').pop();
          if (fileName) {
            await supabase.storage
              .from('public')
              .remove([`property-images/${fileName}`]);
          }
        });

        await Promise.all(deleteStoragePromises);
      }

      // Delete the property (this will cascade delete images due to foreign key constraint)
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Imóvel excluído com sucesso!');
      navigate('/dashboard/listings');
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Erro ao excluir imóvel');
    } finally {
      setIsDeleting(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsLoading(true);

      // Convert currency string to number
      const currencyToNumber = (value: string) => {
        if (!value) return null;
        return parseFloat(value.replace(/[^\d,]/g, '').replace(',', '.'));
      };

      // Get featured image URL
      const featuredImage = images.find(img => img.isFeatured);
      const featuredImageUrl = featuredImage?.url || null;

      // Update property
      const { error: updateError } = await supabase
        .from('properties')
        .update({
          ...values,
          price: currencyToNumber(values.price),
          rent_price: currencyToNumber(values.rent_price),
          featured_offer_price: currencyToNumber(values.featured_offer_price),
          featured_offer_installment: currencyToNumber(values.featured_offer_installment),
          area: parseInt(values.area),
          bedrooms: parseInt(values.bedrooms),
          bathrooms: parseInt(values.bathrooms),
          suites: parseInt(values.suites),
          parking_spaces: parseInt(values.parking_spaces),
          is_visible_on_storefront: values.is_visible_on_storefront,
          featured_image_url: featuredImageUrl,
        })
        .eq('id', id);

      if (updateError) throw updateError;

      // Handle image operations
      await deleteRemovedImages();
      await updateExistingImages();
      await uploadNewImages(id!);

      toast.success('Imóvel atualizado com sucesso!');
      navigate('/dashboard/listings');
    } catch (error) {
      toast.error('Erro ao atualizar imóvel');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <Card>
        <CardHeader>
          <CardTitle>Editar Imóvel</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Título</FormLabel>
                      <FormControl>
                        <Input placeholder="Título do imóvel" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o tipo" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="venda">Venda</SelectItem>
                          <SelectItem value="aluguel">Aluguel</SelectItem>
                          <SelectItem value="venda_aluguel">Venda e Aluguel</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="disponivel">Disponível</SelectItem>
                          <SelectItem value="vendido">Vendido</SelectItem>
                          <SelectItem value="alugado">Alugado</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoria</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a categoria" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Lançamento">Lançamento</SelectItem>
                          <SelectItem value="Na planta">Na planta</SelectItem>
                          <SelectItem value="Em Construção">Em Construção</SelectItem>
                          <SelectItem value="Pronto para morar">Pronto para morar</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preço de Venda</FormLabel>
                      <FormControl>
                        <CurrencyInput placeholder="Valor do imóvel" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rent_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preço do Aluguel (opcional)</FormLabel>
                      <FormControl>
                        <CurrencyInput placeholder="Valor do aluguel" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="featured_offer_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor da Entrada (opcional)</FormLabel>
                      <FormControl>
                        <CurrencyInput placeholder="Valor da entrada" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="featured_offer_installment"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor da Parcela (opcional)</FormLabel>
                      <FormControl>
                        <CurrencyInput placeholder="Valor da parcela" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="area"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Área (m²)</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="Área do imóvel" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bedrooms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Quartos</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="Número de quartos" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bathrooms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Banheiros</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="Número de banheiros" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="suites"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Suítes</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" placeholder="Número de suítes" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="parking_spaces"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vagas de Garagem</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="Número de vagas" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Endereço</FormLabel>
                      <FormControl>
                        <Input placeholder="Endereço completo" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cidade</FormLabel>
                      <FormControl>
                        <Input placeholder="Cidade" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estado</FormLabel>
                      <FormControl>
                        <Input placeholder="Estado" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="zip_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CEP</FormLabel>
                      <FormControl>
                        <Input placeholder="CEP" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Descrição detalhada do imóvel"
                        className="min-h-[120px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="featured_offer_description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição da Oferta (opcional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Detalhes adicionais sobre a oferta especial"
                        className="min-h-[80px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Images Upload Section - Moved to bottom */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium">Imagens do Imóvel</h3>
                  <p className="text-sm text-muted-foreground">
                    Gerencie as fotos do imóvel. A primeira imagem será usada como destaque.
                  </p>
                </div>
                <PropertyImageUpload
                  value={images}
                  onChange={setImages}
                  maxImages={10}
                />
              </div>

              <FormField
                control={form.control}
                name="is_visible_on_storefront"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Mostrar na Vitrine
                      </FormLabel>
                      <FormDescription>
                        Define se o imóvel será visível na sua vitrine pública.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex justify-between items-center space-x-4">
                {/* Delete Button */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      type="button"
                      variant="destructive"
                      disabled={isDeleting || isLoading}
                    >
                      {isDeleting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="mr-2 h-4 w-4" />
                      )}
                      Excluir Imóvel
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Excluir Imóvel</AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta ação não pode ser desfeita. O imóvel e todas as suas imagens serão excluídos permanentemente.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteProperty}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                {/* Action Buttons */}
                <div className="flex space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/dashboard/listings')}
                    disabled={isLoading || isDeleting}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isLoading || isDeleting}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Salvar Alterações
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}